/** @jsx h */
import { component, h } from 'literaljs';

// ── WebGL2 fragment-shader orb ──────────────────────────────
// Self-contained plasma orb rendered behind the hero copy.
// Zero runtime deps. Guards: prefers-reduced-motion, small viewport,
// low device memory / cores, WebGL2 unavailability. Pauses via
// IntersectionObserver when offscreen. Loses context on unmount.

const VERT = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
	v_uv = a_pos * 0.5 + 0.5;
	gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

// SDF plasma sphere with violet/teal palette and mouse-driven tilt.
const FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 outColor;
uniform vec2 u_res;
uniform float u_time;
uniform vec2 u_tilt;

float sdfSphere(vec3 p, float r) { return length(p) - r; }

float noise(vec3 p) {
	vec3 i = floor(p);
	vec3 f = fract(p);
	f = f * f * (3.0 - 2.0 * f);
	float n = i.x + i.y * 57.0 + i.z * 113.0;
	float a = fract(sin(n + 0.0) * 43758.5453);
	float b = fract(sin(n + 1.0) * 43758.5453);
	float c = fract(sin(n + 57.0) * 43758.5453);
	float d = fract(sin(n + 58.0) * 43758.5453);
	return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
	vec2 uv = (v_uv - 0.5);
	uv.x *= u_res.x / u_res.y;
	uv += u_tilt;

	vec3 ro = vec3(0.0, 0.0, 2.4);
	vec3 rd = normalize(vec3(uv, -1.6));

	float t = u_time * 0.35;
	float dist = 1e9;
	float hit = 0.0;
	vec3 p;
	for (int i = 0; i < 48; i++) {
		p = ro + rd * dist;
		float d = sdfSphere(p, 0.85 + 0.12 * noise(p * 2.5 + t));
		if (d < 0.002) { hit = 1.0; break; }
		if (dist > 6.0) break;
		dist += d * 0.85;
	}

	vec3 violet = vec3(0.796, 0.651, 0.969);
	vec3 teal = vec3(0.580, 0.886, 0.835);
	vec3 col = mix(violet, teal, 0.5 + 0.5 * sin(p.x * 2.0 + p.y * 1.5 + t * 1.3));
	col *= 0.55 + 0.45 * noise(p * 3.5 + t * 0.8);
	float glow = exp(-length(uv) * 1.8) * 0.55;
	float alpha = hit * 0.85 + glow * 0.35;
	outColor = vec4(col * (0.6 + glow), clamp(alpha, 0.0, 1.0));
}`;

function compileShader(gl, type, src) {
	const sh = gl.createShader(type);
	gl.shaderSource(sh, src);
	gl.compileShader(sh);
	if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
		gl.deleteShader(sh);
		return null;
	}
	return sh;
}

function setupGL(canvas) {
	const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false });
	if (!gl) return null;
	const vs = compileShader(gl, gl.VERTEX_SHADER, VERT);
	const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAG);
	if (!vs || !fs) return null;
	const prog = gl.createProgram();
	gl.attachShader(prog, vs);
	gl.attachShader(prog, fs);
	gl.linkProgram(prog);
	if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
	const buf = gl.createBuffer();
	gl.bindBuffer(gl.ARRAY_BUFFER, buf);
	gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
	const loc = gl.getAttribLocation(prog, 'a_pos');
	const vao = gl.createVertexArray();
	gl.bindVertexArray(vao);
	gl.enableVertexAttribArray(loc);
	gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
	return { gl, prog, uRes: gl.getUniformLocation(prog, 'u_res'), uTime: gl.getUniformLocation(prog, 'u_time'), uTilt: gl.getUniformLocation(prog, 'u_tilt') };
}

function guardFails() {
	if (typeof window === 'undefined') return true;
	if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
	if (window.innerWidth < 768) return true;
	if (navigator.deviceMemory && navigator.deviceMemory < 4) return true;
	if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) return true;
	return false;
}

export default component({
	name: 'HomeHeroOrb',
	state: {},
	mounted() {
		if (guardFails()) {
			this._skipAnimation = true;
			return;
		}
		this._canvas = document.querySelector('.hero-orb-canvas');
		if (!this._canvas) return;
		this._raf = null;
		this._visible = true;
		this._tiltX = 0;
		this._tiltY = 0;
		this._targetTiltX = 0;
		this._targetTiltY = 0;
		this._t0 = performance.now();

		this._onMove = (e) => {
			const nx = (e.clientX / window.innerWidth) - 0.5;
			const ny = (e.clientY / window.innerHeight) - 0.5;
			this._targetTiltX = -nx * 0.08;
			this._targetTiltY = -ny * 0.08;
		};
		window.addEventListener('pointermove', this._onMove, { passive: true });

		this._onResize = () => { this._resize(); };
		window.addEventListener('resize', this._onResize);

		this._io = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				this._visible = entry.isIntersecting;
				if (this._visible && !this._raf && !this._skipAnimation) this._loop();
				if (!this._visible && this._raf) this._cancel();
			});
		}, { threshold: 0 });
		this._io.observe(this._canvas);

		this._setupGL();
	},
	unmounted() {
		this._cancel();
		if (this._onMove) window.removeEventListener('pointermove', this._onMove);
		if (this._onResize) window.removeEventListener('resize', this._onResize);
		if (this._io) this._io.disconnect();
		if (this._gl && this._gl.gl) {
			const lose = this._gl.gl.getExtension('WEBGL_lose_context');
			if (lose) lose.loseContext();
		}
		this._gl = null;
	},
	methods() {
		return {
			_setupGL() {
				if (!this._canvas) return;
				let gl;
				try { gl = setupGL(this._canvas); } catch (_) { return; }
				if (!gl) return;
				this._gl = gl;
				this._resize();
				gl.gl.useProgram(gl.prog);
				gl.gl.enable(gl.gl.BLEND);
				gl.gl.blendFunc(gl.gl.SRC_ALPHA, gl.gl.ONE_MINUS_SRC_ALPHA);
				if (!this._skipAnimation) this._loop();
				else this._drawOnce();
			},
			_resize() {
				if (!this._canvas || !this._gl) return;
				const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
				const w = this._canvas.clientWidth || 600;
				const h = this._canvas.clientHeight || 400;
				this._canvas.width = Math.floor(w * dpr);
				this._canvas.height = Math.floor(h * dpr);
				this._gl.gl.viewport(0, 0, this._canvas.width, this._canvas.height);
			},
			_loop() {
				const tick = () => {
					if (!this._gl) return;
					const t = (performance.now() - this._t0) / 1000;
					this._tiltX += (this._targetTiltX - this._tiltX) * 0.05;
					this._tiltY += (this._targetTiltY - this._tiltY) * 0.05;
					const { gl, prog, uRes, uTime, uTilt } = this._gl;
					const canvas = gl.canvas;
					gl.useProgram(prog);
					gl.uniform2f(uRes, canvas.width, canvas.height);
					gl.uniform1f(uTime, t);
					gl.uniform2f(uTilt, this._tiltX, this._tiltY);
					gl.clearColor(0, 0, 0, 0);
					gl.clear(gl.COLOR_BUFFER_BIT);
					gl.drawArrays(gl.TRIANGLES, 0, 6);
					this._raf = requestAnimationFrame(tick);
				};
				this._raf = requestAnimationFrame(tick);
			},
			_cancel() {
				if (this._raf) cancelAnimationFrame(this._raf);
				this._raf = null;
			},
			_drawOnce() {
				if (!this._gl) return;
				const t = (performance.now() - this._t0) / 1000;
				const { gl, prog, uRes, uTime, uTilt } = this._gl;
				const canvas = gl.canvas;
				gl.useProgram(prog);
				gl.uniform2f(uRes, canvas.width, canvas.height);
				gl.uniform1f(uTime, t);
				gl.uniform2f(uTilt, 0, 0);
				gl.clearColor(0, 0, 0, 0);
				gl.clear(gl.COLOR_BUFFER_BIT);
				gl.drawArrays(gl.TRIANGLES, 0, 6);
			}
		};
	},
	render() {
		return <canvas class="hero-orb-canvas" aria-hidden="true"></canvas>;
	}
});